import { IPage } from 'models/common';
import {
	IOfficesAction,
	IOfficesData,
	IOfficeListItem,
	IOfficesSearchRequest,
	IOfficesResponse,
	ISearchGeoCoordinates,
} from 'models/shop/office';
import moment = require('moment');
import { IStore } from 'redux/IStore';

/** Action Types */
export const REQUEST_START: string = 'offices/REQUEST_START';
export const REQUEST_FAILURE: string = 'offices/REQUEST_FAILURE';
export const GET_OFFICES_SUCCESS: string = 'offices/GET_OFFICES_SUCCESS';
export const SET_ITEMS_PAGE: string = 'offices/SET_ITEMS_PAGE';
export const RESET_DATA: string = 'offices/RESET_DATA';
export const SET_LAST_SEARCHED_KEYWORD: string = 'offices/SET_LAST_SEARCHED_KEYWORD';
export const SET_SHOPTYPE_FILTER: string = 'offices/SET_SHOPTYPE_FILTER';
export const SET_SEARCH_FILTER: string = 'offices/SET_SEARCH_FILTER';
export const SET_LOCATION_FILTER: string = 'offices/SET_LOCATION_FILTER';
export const SET_PREVIOUSLOCATION_FILTER: string = 'offices/SET_PREVIOUSLOCATION_FILTER';
export const SET_DATE_FILTER: string = 'offices/SET_DATE_FILTER';
export const SET_TIME_FILTER: string = 'offices/SET_TIME_FILTER';
export const SET_SORT_FILTER: string = 'offices/SET_SORT_FILTER';
export const SET_ISFIRSTLOAD: string = 'offices/SET_ISFIRSTLOAD';
export const RESET_FILTERS: string = 'offices/RESET_FILTERS';
export const SET_GEOCOORDINATES: string = 'offices/SET_GEOCOORDINATES';

const itemsPerPage = 50;
/** Initial State */
const initialState: IOfficesData = {
	items: [],
	isFetching: false,
	itemsPage: {
		currentPage: 1,
		totalPages: 0,
		totalItems: 0,
		itemsPerPage,
		items: [],
	},
	keywordFilter: '',
	dateFilter: null,
	timeFilter: null,
	sortFilter: null,
	shopTypes: [],
	lastSearchedKeyword: null,
	isFirstLoad: false,
	geoCoordinates: null,
	isFilteringLocation: false,
	previousGeoCoordinates: null,
};

/** Reducer */
export function officesReducer(state = initialState, action: IOfficesAction): IOfficesData {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case GET_OFFICES_SUCCESS:
			return Object.assign({}, state, {
				items: action.payload.items,
				isFetching: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case SET_ITEMS_PAGE:
			return Object.assign({}, state, {
				itemsPage: action.payload.itemsPage,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				items: [],
				itemsPage: {
					currentPage: 1,
					totalPages: 0,
					totalItems: 0,
					itemsPerPage,
					items: [],
				},
				userId: null,
			});

		case RESET_FILTERS:
			return Object.assign({}, state, {
				keywordFilter: '',
				sortFilter: null,
				statusFilter: 'allposts',
			});

		case SET_SHOPTYPE_FILTER:
			return Object.assign({}, state, {
				sortFilter: 0,
				shopTypes: action.payload.shopTypes,
				isFirstLoad: true,
			});

		case SET_SEARCH_FILTER:
			return Object.assign({}, state, {
				keywordFilter: action.payload.keywordFilter,
				isFirstLoad: true,
			});

		case SET_LOCATION_FILTER:
			return Object.assign({}, state, {
				isFilteringLocation: action.payload.isFilteringLocation,
				isFirstLoad: true,
			});

		case SET_PREVIOUSLOCATION_FILTER:
			return Object.assign({}, state, {
				previousGeoCoordinates: {
					latitude: action.payload.previousGeoCoordinates.latitude,
					longitude: action.payload.previousGeoCoordinates.longitude,
				},
				isFirstLoad: true,
			});

		case SET_DATE_FILTER:
			return Object.assign({}, state, {
				dateFilter: action.payload.dateFilter,
				isFirstLoad: true,
			});

		case SET_TIME_FILTER:
			return Object.assign({}, state, {
				timeFilter: {
					openTime: action.payload.timeFilter?.openTime || null,
					closeTime: action.payload.timeFilter?.closeTime || null,
					utcOffset: action.payload.timeFilter?.utcOffset || null,
				},
				isFirstLoad: true,
			});

		case SET_SORT_FILTER:
			return Object.assign({}, state, {
				sortFilter: action.payload.sortFilter,
				isFirstLoad: true,
			});

		case SET_ISFIRSTLOAD:
			return Object.assign({}, state, {
				isFirstLoad: action.payload.isFirstLoad,
			});

		case SET_LAST_SEARCHED_KEYWORD:
			return Object.assign({}, state, {
				lastSearchedKeyword: action.payload.lastSearchedKeyword,
			});

		case SET_GEOCOORDINATES:
			return Object.assign({}, state, {
				geoCoordinates: action.payload.geoCoordinates,
			});

		default:
			return state;
	}
}

function actionCreatorWithSearch(action: IOfficesAction) {
	return (dispatch) => {
		dispatch(action);
		dispatch(
			setItemsPage({
				currentPage: 1,
				totalPages: 0,
				totalItems: 0,
				itemsPerPage,
				items: [],
			}),
		);
		dispatch(searchOffices(false, true));
	};
}

/** Action Creator */
export function requestStart(): IOfficesAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function getOfficesSuccess(items: IOfficeListItem[]): IOfficesAction {
	return {
		type: GET_OFFICES_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function requestFailure(message: any): IOfficesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function setItemsPage(itemsPage: IPage): IOfficesAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

/** Action Creator */
export function resetData(): IOfficesAction {
	return {
		type: RESET_DATA,
	};
}

/** Action Creator */
export function setLastSearchedKeyword(lastSearchedKeyword: string): IOfficesAction {
	return {
		type: SET_LAST_SEARCHED_KEYWORD,
		payload: {
			lastSearchedKeyword,
		},
	};
}

export function setShopTypeFilter(shopTypes: number[]) {
	return actionCreatorWithSearch({
		type: SET_SHOPTYPE_FILTER,
		payload: {
			shopTypes,
		},
	});
}

export function setSearchFilter(keywordFilter: string) {
	return actionCreatorWithSearch({
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	});
}

export function setSearchFilterKeyword(keywordFilter: string) {
	return {
		type: SET_SEARCH_FILTER,
		payload: {
			keywordFilter,
		},
	};
}

export function setDateFilter(dateFilter: number) {
	return actionCreatorWithSearch({
		type: SET_DATE_FILTER,
		payload: {
			dateFilter,
		},
	});
}

export function setTimeFilter(timeFilter: { openTime?: number; closeTime?: number }) {
	return actionCreatorWithSearch({
		type: SET_TIME_FILTER,
		payload: {
			timeFilter,
		},
	});
}

export function setSortFilter(sortFilter: number) {
	return actionCreatorWithSearch({
		type: SET_SORT_FILTER,
		payload: {
			sortFilter,
		},
	});
}

export function setLocationFilteringStatus(status: boolean): IOfficesAction {
	return {
		type: SET_LOCATION_FILTER,
		payload: {
			isFilteringLocation: status,
		},
	};
}

export function setPreviousLocation(latitude: number, longitude: number): IOfficesAction {
	return {
		type: SET_PREVIOUSLOCATION_FILTER,
		payload: {
			previousGeoCoordinates: {
				latitude,
				longitude,
			},
		},
	};
}

export function setLocationFilter(geoCoordinates: ISearchGeoCoordinates) {
	return actionCreatorWithSearch({
		type: SET_GEOCOORDINATES,
		payload: {
			geoCoordinates,
		},
	});
}

export function setLocationFilterCoordinates(geoCoordinates: ISearchGeoCoordinates) {
	return {
		type: SET_GEOCOORDINATES,
		payload: {
			geoCoordinates,
		},
	};
}

export function resetFilters(): IOfficesAction {
	return {
		type: RESET_FILTERS,
	};
}

export function setIsFirstLoad(isFirstLoad: boolean): IOfficesAction {
	return {
		type: SET_ISFIRSTLOAD,
		payload: {
			isFirstLoad,
		},
	};
}

let validRequest: number;
let oldAbortController: AbortController | undefined;

export function searchOffices(isFirstLoad?: boolean, isRefinement?: boolean) {
	return (dispatch, getState: () => IStore) => {
		const state = getState().offices;
		if (isFirstLoad) {
			dispatch(resetData());
		}
		if (Boolean(state.isFirstLoad) !== Boolean(isFirstLoad)) {
			dispatch(setIsFirstLoad(isFirstLoad));
		}

		dispatch(requestStart());

		const currentPage: number = state.itemsPage.currentPage;
		const newPage: number = currentPage + (isRefinement ? 0 : 1);

		const searchRequest: IOfficesSearchRequest = {
			currentPage: newPage,
			itemsPerPage,
			orderBy: state.sortFilter,
			shopTypes: state.shopTypes || [],
			// API has limitation that it cannot filter by time if dayOfWeek isn't set, hence the below implementation for dayOfWeek
			dayOfWeek:
				state.dateFilter || state.dateFilter === 0
					? state.dateFilter
					: state.timeFilter?.openTime && state.timeFilter?.closeTime
					  ? moment(new Date()).locale('en-us').day()
					  : null,
			openTime: state.timeFilter?.openTime ? state.timeFilter.openTime / 60 : null,
			closeTime: state.timeFilter?.closeTime ? state.timeFilter.closeTime / 60 : null,
			// refinementGroups: state.
		};

		if (state.keywordFilter && state.keywordFilter !== '') {
			searchRequest.searchKeyword = state.keywordFilter;
		}
		if (state.geoCoordinates) {
			searchRequest.geoLocationLatitude = state.geoCoordinates.latitude;
			searchRequest.geoLocationLongitude = state.geoCoordinates.longitude;
			searchRequest.maxDistance = state.geoCoordinates.distance;
		}

		const thisRequest = Math.random();
		validRequest = thisRequest;

		if (typeof AbortController !== 'undefined') {
			if (oldAbortController && !oldAbortController.signal.aborted) {
				oldAbortController.abort();
			}
			oldAbortController = new AbortController();
		}

		return fetch(
			getState().fetchDefault.baseAPI + 'api/shop/offices/search',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(searchRequest),
				signal: oldAbortController ? oldAbortController.signal : undefined,
			}),
		)
			.then((res) => {
				if (validRequest !== thisRequest) {
					return;
				}
				if (res.ok) {
					return res.json().then((res: IOfficesResponse) => {
						const offices: IOfficeListItem[] = (res.offices.items || []).map((office) => ({
							...office,
						}));
						const oldState: IOfficesData = { ...state };
						let items = oldState.items;
						if (isRefinement || isFirstLoad) {
							items = [];
						}
						const newItems: IOfficeListItem[] = offices || [];
						Array.prototype.push.apply(items, newItems);
						dispatch(getOfficesSuccess(items));
						dispatch(setItemsPage(res.offices));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				if ((oldAbortController && oldAbortController.signal.aborted) || validRequest !== thisRequest) {
					return;
				}
				dispatch(requestFailure(err));
			});
	};
}
